import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useAxios, useStoredState } from "../../hooks";
import { IReferralDashboardContext, ReferralDashboardContext } from ".";
import { ReferralDashboardData } from "../../models";
import { Endpoints } from "../../api";
import { useTerritories } from "..";

export const ReferralDashboardProvider = (props: PropsWithChildren) => {
    const { selectedTerritoryIds } = useTerritories();
    const { get } = useAxios();
    const [mode, setMode] = useStoredState<"classic" | "new">("referralDashboardMode", "new");
    const [dashboardData, setDashboardData] = useState<ReferralDashboardData | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const getDashboardData = useCallback(async () => {
        setIsLoading(true);
        const data = await get<ReferralDashboardData>({
            url: `${Endpoints.Referrals}?territoryIds=${selectedTerritoryIds.join(",")}`,
        });
        setDashboardData(data);
        setIsLoading(false);
    }, [get, selectedTerritoryIds]);

    const toggleMode = useCallback(() => {
        setMode(mode === "classic" ? "new" : "classic");
    }, [mode, setMode]);

    const [showCallBackModal, setShowCallBackModal] = useState<boolean>(false);
    const [activeCallBackId, setActiveCallBackId] = useState<number>(0);
    const allCallBacks = useMemo(
        () => (dashboardData ? [...dashboardData.activeCallBacks, ...dashboardData.qaCallBacks] : []),
        [dashboardData]
    );
    const activeCallBack = useMemo(
        () => allCallBacks.find((cb) => cb.id === activeCallBackId),
        [allCallBacks, activeCallBackId]
    );

    useEffect(() => {
        getDashboardData();
    }, [getDashboardData]);

    const handleShowCallBackModal = (callBackId: number) => {
        setActiveCallBackId(callBackId);
        setShowCallBackModal(true);
    };

    const handleCloseCallBackModal = useCallback(() => {
        setShowCallBackModal(false);
        setActiveCallBackId(0);
        getDashboardData();
    }, [getDashboardData]);

    const value = useMemo<IReferralDashboardContext>(
        () => ({
            getDashboardData,
            dashboardData,
            isLoading,
            mode,
            toggleMode,
            handleShowCallBackModal,
            handleCloseCallBackModal,
            showCallBackModal,
            activeCallBack,
        }),
        [
            activeCallBack,
            dashboardData,
            getDashboardData,
            handleCloseCallBackModal,
            isLoading,
            mode,
            showCallBackModal,
            toggleMode,
        ]
    );

    return <ReferralDashboardContext.Provider value={value} {...props} />;
};
